import { Component, OnDestroy, OnInit } from '@angular/core';
import { Location } from '@angular/common';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Subscription } from 'rxjs';
import { LexbeCase } from '../../../models/case.model';
import { CustomCaseFieldValue } from '../../../models/custom-case-field-value.model';
import { UserConfigs } from '../../../models/user-configs.model';
import { CasesService } from '../../../services/cases.service';
import { UserConfigsService } from '../../../services/user-configs.service';
import { HelpService } from '../../../services/help.service';
import { CustomCaseFieldValueService } from '../../../services/custom-case-field-value.service';
import { PatchDeltaObjectUtility } from '../../../utilities/patch-delta-object.utility';

@Component({
    selector: 'lex-case-details-edit',
    templateUrl: './case-details-edit.component.html',
    styleUrls: ['./case-details-edit.component.scss'],
    standalone: false
})
export class CaseDetailsEditComponent implements OnInit, OnDestroy {
	public caseDetailsForm: UntypedFormGroup;
	public currentCase: LexbeCase;
	private subscription = new Subscription();

	constructor(
		private route: ActivatedRoute,
		private location: Location,
		private formBuilder: UntypedFormBuilder,
		private snackBar: MatSnackBar,
		private userService: UserConfigsService,
		private caseService: CasesService,
		private helpService: HelpService,
		private customCaseFieldValueService: CustomCaseFieldValueService
	) {
	}

	ngOnInit(): void {
		this.route.params.subscribe(params => {
			if (params.id) {
				// Get case to edit from CaseId in URL.
				this.caseService.getCase(params.id).subscribe((currentCase: LexbeCase) => {
					this.currentCase = currentCase;
					this.bindForm(currentCase);
				});
			} else {
				// Get case to edit from user's current case.
				this.subscription.add(this.userService.getUserConfigs().subscribe((config: UserConfigs) => {
					this.currentCase = config.currentCase;
					this.bindForm(config.currentCase);
				}));
			}
		});

		this.helpService.updateHelpLink('home-menu/case-details');
	}

	ngOnDestroy(): void {
		this.subscription.unsubscribe();
	}

	public onSubmit(): void {
		// Validation
		if (this.caseDetailsForm.invalid) {
			this.snackBar.open('Please fix the case details before saving.', 'X');
			return;
		}

		// Update case properties
		const jobPatch = PatchDeltaObjectUtility.getDeltaObject<LexbeCase>(this.caseDetailsForm.controls);

		if (!this.currentCase.customFields)
			return;

		const currentCustomFields = this.currentCase.customFields;
		for (let i = 0; i < currentCustomFields.length; i++) {
			const field = currentCustomFields[i];
			if (field.type === 0)
				continue;

			const updatedValue = this.caseDetailsForm.value[field.fieldId];
			if (field.type === 1 && field.text !== updatedValue) {
				field.text = updatedValue;
				this.customCaseFieldValueService.update(field).subscribe();
			} else if (field.type === 2 && field.text !== updatedValue) {
				field.checked = updatedValue;
				this.customCaseFieldValueService.update(field).subscribe();
			}
		}

		this.caseService.updateCase(this.currentCase.caseId, jobPatch).subscribe(() => {
			this.location.back();
		});
	}

	public onCancel(): void {
		this.location.back();
	}

	private bindForm(currentCase: LexbeCase): void {
		this.caseDetailsForm = this.formBuilder.group({
			caseName: [currentCase.caseName, [Validators.required, Validators.maxLength(256)]],
			caseNumber: [currentCase.caseNumber, Validators.maxLength(256)],
			caseType: [currentCase.caseType, Validators.maxLength(256)],
			caseVenue: [currentCase.caseVenue, Validators.maxLength(256)],
			leadAttorney: [currentCase.leadAttorney, Validators.maxLength(256)],
			trierOfFact: [currentCase.trierOfFact, Validators.maxLength(256)],
			customDesignation: [currentCase.customDesignation, Validators.maxLength(256)],
			caseComments: [currentCase.caseComments, Validators.maxLength(1024)],
			caseKeywords: [currentCase.caseKeywords]
		});

		if (!currentCase.customFields)
			return;

		// Add custom case fields to the form.
		currentCase.customFields.forEach((field: CustomCaseFieldValue) => {
			if (field.type === 1) {
				// Textbox
				this.caseDetailsForm.addControl(
					field.fieldId,
					this.formBuilder.control(field.text, Validators.maxLength(256))
				);
			} else if (field.type === 2) {
				// Checkbox
				this.caseDetailsForm.addControl(
					field.fieldId,
					this.formBuilder.control(field.checked)
				);
			}
		});
	}

}
